/**
 * RevisionCasCCS.jsx — Mode Révision Pro des 27 études de cas CCS 2 Roues.
 *
 * Route : /enseignant/revision-cas-ccs
 *
 * Quatre modules d'apprentissage progressifs accessibles depuis un hub :
 *   1. 🃏 FLASHCARDS  — Swipe Tinder-style + mémoire espacée (Leitner)
 *   2. 🎯 QUIZ FAMILLE — Identifier la famille pédagogique (chrono + combo)
 *   3. 🧠 MÉCANIQUE   — Tutoriel 4 étapes pour APPRENDRE le raisonnement
 *   4. 📊 PROGRESSION — Tableau de bord (maîtrise par cas, streak, badges)
 *
 * Tout est piloté par le hook `useRevisionCas` (état + localStorage).
 * Pas de backend.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Sparkles, Brain, Flame, Trophy, Target, RotateCcw,
  CheckCircle2, XCircle, ChevronLeft, ChevronRight, Lightbulb, BookOpen,
  TimerReset, Layers, Zap, Award, Hand,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CAS_DETAILS } from '../data/ccs2r/casDetails';
import { FAMILIES } from '../data/ccs2r/classeur7Familles';
import useSeo from '../hooks/useSeo';
import { useRevisionCas } from '../hooks/useRevisionCas';
import {
  usePresentationMode,
  PresentationButton,
  PresentationOverlay,
} from '../components/PresentationMode';

// ─────────────────────────────────────────────────────────────────────────────
// Utils
// ─────────────────────────────────────────────────────────────────────────────

const ALL_CAS_NUMS = CAS_DETAILS.map((c) => c.num);

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function getFamilyMeta(slug) {
  return FAMILIES.find((f) => f.slug === slug) || { title: slug, color: '#7c3aed' };
}

const LEVEL_COLORS = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#16a34a', '#0891b2'];
const LEVEL_LABELS = ['Nouveau', 'Découvert', 'En cours', 'Confirmé', 'Maîtrisé', 'Mémoire long terme'];

// ─────────────────────────────────────────────────────────────────────────────
// HUB — Vue d'ensemble
// ─────────────────────────────────────────────────────────────────────────────

function Hub({ stats, setMode }) {
  const masteredPct = Math.round((stats.nbMastered / CAS_DETAILS.length) * 100);

  return (
    <div className="space-y-6">
      {/* CTA Grand Quiz Studio — bannière proéminente (compacte mobile / riche desktop) */}
      <Link
        to="/enseignant/grand-quiz"
        data-testid="cta-grand-quiz"
        className="block group relative overflow-hidden rounded-2xl sm:rounded-3xl p-3 sm:p-6 transition-all hover:-translate-y-0.5 hover:shadow-2xl active:scale-[0.99]"
        style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #cf2031 50%, #7c3aed 100%)',
          boxShadow: '0 10px 30px -10px rgba(245,158,11,0.4)',
        }}
      >
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full blur-3xl bg-white/10 pointer-events-none hidden sm:block" />
        <div className="relative flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <Trophy className="w-5 h-5 sm:w-8 sm:h-8 text-white" strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0 text-white">
            <div className="text-[9px] sm:text-[11px] font-black uppercase tracking-[0.08em] sm:tracking-[0.18em] opacity-90 truncate">
              Studio TV pour la classe
            </div>
            <div
              className="text-sm sm:text-2xl font-black leading-tight mt-0.5"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              <span className="sm:hidden">Grand Quiz Studio</span>
              <span className="hidden sm:inline">Grand Quiz Studio · Projection collective</span>
            </div>
            <p className="hidden sm:block text-xs sm:text-sm opacity-95 mt-1 leading-relaxed">
              Mode équipes 2-4 · 5 mécaniques · paliers ×100→×400 · jokers · zéro smartphone élève
            </p>
          </div>
          <div className="flex-shrink-0 inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-4 py-1.5 sm:py-2.5 rounded-full bg-white text-[#cf2031] text-[10px] sm:text-sm font-black uppercase tracking-wider shadow-lg group-hover:gap-2 transition-all">
            <span className="hidden sm:inline">Lancer</span>
            <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </div>
        </div>
      </Link>

      {/* CTA Fiche de Séance Live (compacte mobile / riche desktop) */}
      <Link
        to="/enseignant/fiche-seance-live"
        data-testid="cta-fiche-seance-live"
        className="block group relative overflow-hidden rounded-2xl sm:rounded-3xl p-3 sm:p-6 transition-all hover:-translate-y-0.5 hover:shadow-2xl active:scale-[0.99]"
        style={{
          background: 'linear-gradient(135deg, #0891b2 0%, #1d4ed8 55%, #16a34a 100%)',
          boxShadow: '0 10px 30px -10px rgba(29,78,216,0.4)',
        }}
      >
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full blur-3xl bg-white/10 pointer-events-none hidden sm:block" />
        <div className="relative flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 sm:w-8 sm:h-8 text-white" strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0 text-white">
            <div className="text-[9px] sm:text-[11px] font-black uppercase tracking-[0.08em] sm:tracking-[0.18em] opacity-90 truncate">
              Nouveau · Co-construction pédagogique
            </div>
            <div
              className="text-sm sm:text-2xl font-black leading-tight mt-0.5"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Construis ta séance LIVE
            </div>
            <p className="hidden sm:block text-xs sm:text-sm opacity-95 mt-1 leading-relaxed">
              Chaque bonne réponse remplit le canevas REMC officiel · 7 ou 9 étapes · projection + export PDF
            </p>
          </div>
          <div className="flex-shrink-0 inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-4 py-1.5 sm:py-2.5 rounded-full bg-white text-[#1d4ed8] text-[10px] sm:text-sm font-black uppercase tracking-wider shadow-lg group-hover:gap-2 transition-all">
            <span className="hidden sm:inline">Construire</span>
            <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </div>
        </div>
      </Link>

      {/* HERO */}
      <section
        className="relative overflow-hidden rounded-3xl bg-card border border-border/60"
        data-testid="revision-hero"
        style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 16px 40px -20px rgba(207,32,49,0.25)' }}
      >
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: '#cf2031' }} aria-hidden="true" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full blur-3xl opacity-15 pointer-events-none" style={{ background: '#7c3aed' }} aria-hidden="true" />
        <div className="relative p-6 sm:p-9">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#cf2031]/15 border border-[#cf2031]/30 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-[#cf2031] mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Mode Révision Pro · 27 études CCS
          </div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            data-testid="revision-title"
          >
            Apprends le contenu<br />
            <span className="text-[#cf2031]">ET la mécanique</span> de l&apos;oral.
          </h1>
          <p className="text-sm sm:text-base text-foreground/80 mt-3 max-w-3xl leading-relaxed">
            <strong>4 modes d&apos;entraînement</strong> qui se renforcent mutuellement : flashcards à mémoire espacée
            pour ancrer les 27 cas, quiz éclair pour la vitesse de reconnaissance, et tutoriel raisonnement
            pour <strong>apprendre la séquence d&apos;analyse</strong> qui te servira face au jury.
          </p>

          {/* Mini-stats */}
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 max-w-3xl" data-testid="hub-stats">
            <StatBox label="Maîtrisés" value={`${stats.nbMastered} / 27`} color="#16a34a" icon={Trophy} />
            <StatBox label="En cours" value={stats.nbInProgress} color="#eab308" icon={Brain} />
            <StatBox label="À revoir aujourd'hui" value={stats.dueCount} color="#cf2031" icon={TimerReset} />
            <StatBox label="Streak" value={`${stats.streak.current} j`} color="#f97316" icon={Flame} />
          </div>

          {/* Barre de progression */}
          <div className="mt-5">
            <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1.5">
              <span>Maîtrise globale</span>
              <span>{masteredPct} %</span>
            </div>
            <div className="h-2.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full transition-all duration-700"
                style={{ width: `${masteredPct}%`, background: 'linear-gradient(90deg,#cf2031,#16a34a)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* 4 MODES */}
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="modes-grid">
        <ModeCard
          onClick={() => setMode('flashcards')}
          accent="#cf2031"
          icon={Layers}
          kicker="Mode 1 · Mémorisation"
          title="🃏 Flashcards Tinder-style"
          desc="Swipe ← « À revoir » ou → « Maîtrisé ». La mémoire espacée fait revenir les cas au bon moment (J+1, J+3, J+7, J+21)."
          badge={`${stats.dueCount} à revoir`}
          testid="mode-flashcards"
        />
        <ModeCard
          onClick={() => setMode('quiz-famille')}
          accent="#7c3aed"
          icon={Zap}
          kicker="Mode 2 · Vitesse"
          title="🎯 Quiz famille éclair"
          desc="Identifie la famille pédagogique parmi 7. Chrono, combos, et best score. Apprends la classification mentale des 27 cas."
          badge={stats.quizBest.famille.total > 0 ? `Best ${stats.quizBest.famille.score}/${stats.quizBest.famille.total}` : 'Nouveau'}
          testid="mode-quiz-famille"
        />
        <ModeCard
          onClick={() => setMode('mecanique')}
          accent="#0891b2"
          icon={Brain}
          kicker="Mode 3 · Raisonnement"
          title="🧠 Mécanique 4 étapes"
          desc="LA méthode d'analyse d'un cas décomposée : famille → diagnostic → objectifs → évaluation. Indispensable avant l'oral."
          badge="Tutoriel"
          testid="mode-mecanique"
        />
        <ModeCard
          onClick={() => setMode('progression')}
          accent="#16a34a"
          icon={Trophy}
          kicker="Mode 4 · Suivi"
          title="📊 Ma progression"
          desc="Maîtrise détaillée des 27 cas, niveaux Leitner par carte, badges et historique des sessions."
          badge={`${stats.sessionsCount} session${stats.sessionsCount > 1 ? 's' : ''}`}
          testid="mode-progression"
        />
      </section>
    </div>
  );
}

const StatBox = ({ label, value, color, icon: Icon }) => (
  <div className="rounded-2xl bg-background/40 border border-border/40 backdrop-blur p-2.5 sm:p-3">
    <div className="flex items-center gap-1.5 mb-1">
      <Icon className="w-3 h-3" style={{ color }} />
      <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.14em] text-foreground/65">{label}</span>
    </div>
    <div className="text-2xl sm:text-3xl font-black tabular-nums leading-none" style={{ color, fontFamily: "'Playfair Display', Georgia, serif" }}>
      {value}
    </div>
  </div>
);

const ModeCard = ({ onClick, accent, icon: Icon, kicker, title, desc, badge, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="group relative overflow-hidden rounded-3xl bg-card border border-border/60 p-5 sm:p-6 text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
    style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 8px 24px -16px rgba(0,0,0,0.12)' }}
  >
    <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl opacity-15 group-hover:opacity-30 transition-opacity duration-500 pointer-events-none" style={{ background: accent }} />
    <div className="relative flex items-start justify-between gap-2 mb-3">
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3" style={{ background: accent, boxShadow: `0 8px 20px -8px ${accent}55` }}>
        <Icon className="w-6 h-6" strokeWidth={2.2} />
      </div>
      <span className="text-[10px] font-black uppercase tracking-[0.14em] rounded-full px-2.5 py-1" style={{ background: `${accent}1a`, color: accent }}>
        {badge}
      </span>
    </div>
    <div className="relative">
      <div className="text-[10px] font-black uppercase tracking-[0.18em] mb-1" style={{ color: accent }}>{kicker}</div>
      <h3 className="text-lg sm:text-xl font-black leading-tight tracking-tight">{title}</h3>
      <p className="text-xs sm:text-sm text-muted-foreground mt-2 leading-relaxed">{desc}</p>
      <div className="flex items-center gap-1.5 text-xs font-bold mt-4 group-hover:gap-2.5 transition-all duration-300" style={{ color: accent }}>
        Commencer <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
      </div>
    </div>
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// MODE 1 — FLASHCARDS swipe
// ─────────────────────────────────────────────────────────────────────────────

function FlashcardsMode({ revision, onBack }) {
  const due = useMemo(() => revision.getDueToday(ALL_CAS_NUMS), [revision]);
  // Si rien de dû → propose les cas niveau le plus bas
  const queueNums = useMemo(() => {
    if (due.length > 0) return due.map((d) => d.num);
    // fallback : ordre par level ASC
    return [...ALL_CAS_NUMS].sort((a, b) => {
      const la = revision.state.cards[a]?.level ?? 0;
      const lb = revision.state.cards[b]?.level ?? 0;
      return la - lb;
    });
  }, [due, revision.state.cards]);

  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [done, setDone] = useState({ hits: 0, total: 0 });
  const [swipeDelta, setSwipeDelta] = useState(0);
  const touchStartX = useRef(null);

  const currentNum = queueNums[idx];
  const cas = CAS_DETAILS.find((c) => c.num === currentNum);
  const famMeta = cas ? getFamilyMeta(cas.familySlug) : null;
  const card = cas ? revision.state.cards[cas.num] : null;

  const handle = (success) => {
    if (!cas) return;
    revision.recordCard(cas.num, success);
    setDone((d) => ({ hits: d.hits + (success ? 1 : 0), total: d.total + 1 }));
    setRevealed(false);
    setSwipeDelta(0);
    if (idx + 1 >= queueNums.length) {
      revision.recordSession({
        mode: 'flashcards',
        startedAt: new Date().toISOString(),
        total: done.total + 1,
        hits: done.hits + (success ? 1 : 0),
      });
      setIdx(idx + 1);
    } else {
      setIdx(idx + 1);
    }
  };

  // Swipe tactile mobile (verso uniquement)
  const onTouchStart = (e) => {
    if (!revealed) return;
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchMove = (e) => {
    if (!revealed || touchStartX.current == null) return;
    setSwipeDelta(e.touches[0].clientX - touchStartX.current);
  };
  const onTouchEnd = () => {
    if (!revealed || touchStartX.current == null) return;
    const dx = swipeDelta;
    touchStartX.current = null;
    if (dx > 100) handle(true);
    else if (dx < -100) handle(false);
    else setSwipeDelta(0);
  };

  if (!cas || idx >= queueNums.length) {
    return (
      <FinishScreen
        title="🎉 Session terminée"
        subtitle={`${done.hits} / ${done.total} cas marqués comme maîtrisés`}
        accent="#cf2031"
        onBack={onBack}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="flashcards-mode">
      <SessionHeader
        onBack={onBack}
        title="🃏 Flashcards"
        progress={`${idx + 1} / ${queueNums.length}`}
        accent="#cf2031"
      />

      <div
        className="relative mx-auto max-w-2xl bg-card border-2 rounded-3xl overflow-hidden shadow-2xl transition-transform duration-200 touch-pan-y"
        style={{
          borderColor: famMeta.color,
          minHeight: 420,
          transform: revealed ? `translateX(${swipeDelta}px) rotate(${swipeDelta * 0.04}deg)` : undefined,
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        data-testid="flashcard"
      >
        {/* Indicateurs swipe (verso uniquement) */}
        {revealed && Math.abs(swipeDelta) > 30 && (
          <div
            className="absolute top-1/2 -translate-y-1/2 px-5 py-3 rounded-2xl text-2xl font-black uppercase tracking-widest pointer-events-none z-10"
            style={{
              [swipeDelta > 0 ? 'right' : 'left']: '1.5rem',
              background: swipeDelta > 0 ? '#16a34a' : '#cf2031',
              color: 'white',
              opacity: Math.min(Math.abs(swipeDelta) / 100, 1),
            }}
          >
            {swipeDelta > 0 ? '✓ Maîtrisé' : '↺ À revoir'}
          </div>
        )}
        {/* RECTO */}
        {!revealed && (
          <div className="p-6 sm:p-8 flex flex-col h-full min-h-[420px]">
            <div className="flex items-center justify-between mb-4">
              <Badge style={{ background: famMeta.color, color: 'white', border: 'none' }}>
                Cas n°{cas.num}
              </Badge>
              <Badge variant="outline">
                Niveau : {LEVEL_LABELS[card?.level ?? 0]}
              </Badge>
            </div>
            <h2
              className="text-2xl sm:text-3xl font-black leading-tight mb-4 flex-1"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              data-testid="flashcard-title"
            >
              {cas.title}
            </h2>
            <p className="text-sm text-muted-foreground italic">
              « {cas.enonce.slice(0, 180)}... »
            </p>
            <div className="mt-6 flex flex-col items-center gap-2">
              <Button onClick={() => setRevealed(true)} size="lg" className="w-full sm:w-auto gap-2" data-testid="btn-reveal" style={{ background: famMeta.color }}>
                <Hand className="w-4 h-4" /> Révéler la famille pédagogique
              </Button>
              <span className="text-[10px] text-muted-foreground">Devine la famille avant de retourner la carte.</span>
            </div>
          </div>
        )}
        {/* VERSO */}
        {revealed && (
          <div className="p-6 sm:p-8 flex flex-col h-full min-h-[420px]">
            <div className="flex items-center justify-between mb-3">
              <Badge style={{ background: famMeta.color, color: 'white', border: 'none' }}>
                Famille · {famMeta.title}
              </Badge>
              <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: famMeta.color }}>
                Cas n°{cas.num}
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black leading-tight mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {cas.title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
              <Pill label="Public" text={cas.publicAttendu?.split(',')[0]} />
              <Pill label="Aspect" text={famMeta.aspect || famMeta.title} />
              <Pill label="Méthode" text={cas.methodesAttendues?.[0] || '—'} />
            </div>
            <div className="text-xs space-y-2 mb-4 flex-1">
              <div><strong className="text-[#cf2031]">Connaître</strong> · {cas.objectifs?.connaitre}</div>
              <div><strong className="text-[#7c3aed]">Comprendre</strong> · {cas.objectifs?.comprendre}</div>
              <div><strong className="text-[#16a34a]">Agir</strong> · {cas.objectifs?.agir}</div>
            </div>
            <div className="flex gap-2 mt-auto">
              <Button onClick={() => handle(false)} variant="outline" className="flex-1 gap-1.5 border-[#cf2031]/40 text-[#cf2031] hover:bg-[#cf2031]/10" data-testid="btn-revoir">
                <ChevronLeft className="w-4 h-4" /> À revoir
              </Button>
              <Button onClick={() => handle(true)} className="flex-1 gap-1.5" style={{ background: '#16a34a' }} data-testid="btn-maitrise">
                Maîtrisé <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="text-center text-xs text-muted-foreground">
        Mémoire espacée Leitner · Cas vus apparaîtront dans 1, 3, 7, 21 ou 60 jours selon ta progression.
      </div>
    </div>
  );
}

const Pill = ({ label, text }) => (
  <div className="rounded-lg bg-muted/40 border border-border/40 p-2">
    <div className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="text-xs font-bold mt-0.5 truncate" title={text}>{text || '—'}</div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// MODE 2 — QUIZ FAMILLE chrono
// ─────────────────────────────────────────────────────────────────────────────

const QUIZ_DURATION = 10; // 10s par question
const QUIZ_QUESTIONS = 10;

function QuizFamilleMode({ revision, onBack }) {
  const [queue] = useState(() => shuffle(ALL_CAS_NUMS).slice(0, QUIZ_QUESTIONS));
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(QUIZ_DURATION);
  const [feedback, setFeedback] = useState(null); // null | 'correct' | 'wrong' | 'timeout'

  const currentNum = queue[idx];
  const cas = CAS_DETAILS.find((c) => c.num === currentNum);

  // Tirage des 4 options (la bonne + 3 fausses)
  const options = useMemo(() => {
    if (!cas) return [];
    const correctSlug = cas.familySlug;
    const others = FAMILIES.filter((f) => f.slug !== correctSlug);
    const fake = shuffle(others).slice(0, 3);
    return shuffle([
      { slug: correctSlug, correct: true },
      ...fake.map((f) => ({ slug: f.slug, correct: false })),
    ]);
  }, [currentNum]); // eslint-disable-line react-hooks/exhaustive-deps

  // Timer — décrémente chaque seconde et déclenche le timeout à 0
  useEffect(() => {
    if (feedback) return undefined;
    const t = setTimeout(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          // s va passer à 0 : on déclenche le timeout dans le même tick
          setFeedback('timeout');
          setCombo(0);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, feedback]);

  const handleChoice = (opt) => {
    if (feedback) return;
    if (opt.correct) {
      const newCombo = combo + 1;
      const bonus = Math.min(newCombo, 5);
      setScore((s) => s + 100 + bonus * 20);
      setCombo(newCombo);
      setBestCombo((b) => Math.max(b, newCombo));
      setFeedback('correct');
    } else {
      setCombo(0);
      setFeedback('wrong');
    }
  };

  const handleNext = () => {
    if (idx + 1 >= queue.length) {
      revision.recordSession({
        mode: 'quiz-famille',
        total: queue.length,
        hits: score / 100, // approximatif
        score,
        bestCombo,
      });
      setIdx(idx + 1);
      return;
    }
    setIdx(idx + 1);
    setSecondsLeft(QUIZ_DURATION);
    setFeedback(null);
  };

  if (!cas || idx >= queue.length) {
    const hits = Math.round(score / 100);
    return (
      <FinishScreen
        title="🎯 Quiz terminé"
        subtitle={`Score : ${score} · Best combo : ${bestCombo}`}
        meta={`${hits} / ${queue.length} bonnes réponses`}
        accent="#7c3aed"
        onBack={onBack}
      />
    );
  }

  const timePct = (secondsLeft / QUIZ_DURATION) * 100;

  return (
    <div className="space-y-4" data-testid="quiz-famille-mode">
      <SessionHeader
        onBack={onBack}
        title="🎯 Quiz famille éclair"
        progress={`Q${idx + 1} / ${queue.length}`}
        accent="#7c3aed"
        right={
          <div className="flex items-center gap-3 text-xs font-bold">
            <span className="text-[#7c3aed]" data-testid="quiz-score">Score : {score}</span>
            {combo > 0 && <span className="text-[#f97316]" data-testid="quiz-combo">🔥 Combo x{combo}</span>}
          </div>
        }
      />

      {/* TIMER */}
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{
            width: `${timePct}%`,
            background: secondsLeft <= 3 ? '#cf2031' : '#7c3aed',
          }}
          data-testid="quiz-timer-bar"
        />
      </div>

      <Card className="border-2" style={{ borderColor: '#7c3aed' }}>
        <CardContent className="p-5 sm:p-7">
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">
            Cas n°{cas.num} · Identifie la famille pédagogique
          </div>
          <h2
            className="text-xl sm:text-2xl font-black leading-tight mb-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            data-testid="quiz-cas-title"
          >
            {cas.title}
          </h2>
          <p className="text-xs text-muted-foreground italic">« {cas.enonce.slice(0, 200)}... »</p>
        </CardContent>
      </Card>

      {/* OPTIONS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {options.map((opt) => {
          const f = FAMILIES.find((fam) => fam.slug === opt.slug);
          const isCorrectShown = feedback && opt.correct;
          const isWrongShown = feedback === 'wrong' && !opt.correct;
          return (
            <button
              key={opt.slug}
              type="button"
              onClick={() => handleChoice(opt)}
              disabled={!!feedback}
              data-testid={`quiz-opt-${opt.slug}`}
              className={`relative rounded-2xl p-4 text-left border-2 transition-all ${
                !feedback ? 'hover:scale-[1.02] active:scale-[0.98]' : ''
              } ${
                isCorrectShown ? 'border-[#16a34a] bg-[#16a34a]/10' : isWrongShown ? 'border-[#cf2031]/40 opacity-60' : 'border-border bg-card'
              }`}
              style={!feedback ? { borderLeftColor: f.color, borderLeftWidth: 5 } : undefined}
            >
              <div className="flex items-center gap-2">
                <span className="font-black text-sm" style={{ color: f.color }}>{f.title}</span>
                {isCorrectShown && <CheckCircle2 className="w-5 h-5 text-[#16a34a] ml-auto" />}
                {isWrongShown && <XCircle className="w-5 h-5 text-[#cf2031] ml-auto" />}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{f.aspect}</div>
            </button>
          );
        })}
      </div>

      {/* FEEDBACK + NEXT */}
      {feedback && (
        <Card className="border-2" style={{ borderColor: feedback === 'correct' ? '#16a34a' : '#cf2031' }}>
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm font-bold">
              {feedback === 'correct' && <>✅ Bonne réponse ! <span className="text-muted-foreground">+{100 + Math.min(combo, 5) * 20} pts</span></>}
              {feedback === 'wrong' && <>❌ Manqué. Famille correcte : <strong>{getFamilyMeta(cas.familySlug).title}</strong></>}
              {feedback === 'timeout' && <>⏱ Trop tard. Famille correcte : <strong>{getFamilyMeta(cas.familySlug).title}</strong></>}
            </div>
            <Button onClick={handleNext} size="sm" className="gap-1.5" style={{ background: '#7c3aed' }} data-testid="btn-next-quiz">
              {idx + 1 >= queue.length ? 'Voir le score' : 'Suivant'} <ArrowRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODE 3 — MÉCANIQUE (Tutoriel 4 étapes)
// ─────────────────────────────────────────────────────────────────────────────

const MECANIQUE_STEPS = [
  {
    id: 1,
    title: '1. Identifier la famille',
    accent: '#cf2031',
    icon: Layers,
    teach: "Avant tout, classe ton cas dans une des 7 familles pédagogiques. Ça t'oriente immédiatement vers le **type d'objectif pédagogique** (faire évoluer un comportement, faire prendre conscience d'un risque, rendre compréhensible une notion…). Indices : mots-clés du sujet, type d'élèves, contexte.",
    actionLabel: 'Choisir la famille',
  },
  {
    id: 2,
    title: '2. Décider du diagnostic',
    accent: '#7c3aed',
    icon: Brain,
    teach: "Diagnostic = **constat** + **hypothèses**. Que sait-on des élèves ? Quelles sont leurs représentations ? Quels freins / leviers ? Construis ton diag en 2-3 phrases : ne récite pas le sujet, **interprète-le**.",
    actionLabel: 'Voir un diagnostic-modèle',
  },
  {
    id: 3,
    title: '3. Ordonner les 3 objectifs',
    accent: '#0891b2',
    icon: Target,
    teach: "C'est LE squelette de ta séance : Connaître → Comprendre → Agir. **Ordre strict** (Bloom). Chaque objectif intermédiaire doit être **observable** (avec un verbe d'action) et **vérifiable** par une activité.",
    actionLabel: 'Voir les 3 objectifs-modèles',
  },
  {
    id: 4,
    title: '4. Choisir l\'évaluation',
    accent: '#16a34a',
    icon: CheckCircle2,
    teach: "Sans évaluation, pas de séance. Choisis 1 ou 2 modalités qui vérifient **chaque objectif** : QCM rapide, mise en situation, restitution orale, observation guidée. C'est ce qui prouve au jury que tu sais boucler la boucle pédagogique.",
    actionLabel: 'Voir une évaluation-modèle',
  },
];

function MecaniqueMode({ onBack }) {
  // Choisit aléatoirement 1 cas comme exemple fil rouge
  const [casExample] = useState(() => CAS_DETAILS[Math.floor(Math.random() * CAS_DETAILS.length)]);
  const famMeta = getFamilyMeta(casExample.familySlug);
  const [step, setStep] = useState(0);

  const current = MECANIQUE_STEPS[step];

  const renderReveal = () => {
    if (step === 0) {
      return (
        <div className="rounded-2xl p-4 border-2" style={{ borderColor: famMeta.color, background: `${famMeta.color}0d` }}>
          <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: famMeta.color }}>Famille de ce cas</div>
          <div className="text-xl font-black mt-1">{famMeta.title}</div>
          <div className="text-xs text-muted-foreground mt-1">{famMeta.aspect}</div>
        </div>
      );
    }
    if (step === 1) {
      return (
        <div className="rounded-2xl p-4 border-2 border-[#7c3aed]/40 bg-[#7c3aed]/5">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#7c3aed]">Diagnostic-modèle</div>
          <div className="text-sm mt-2 leading-relaxed">
            <strong>Public :</strong> {casExample.publicAttendu}
          </div>
          <div className="text-sm mt-2 leading-relaxed">
            <strong className="text-[#16a34a]">Leviers :</strong> {casExample.leviersAttendus?.slice(0, 3).join(' · ')}
          </div>
          <div className="text-sm mt-1 leading-relaxed">
            <strong className="text-[#cf2031]">Freins :</strong> {casExample.freinsAttendus?.slice(0, 3).join(' · ')}
          </div>
        </div>
      );
    }
    if (step === 2) {
      return (
        <div className="space-y-2">
          {['connaitre', 'comprendre', 'agir'].map((k, i) => {
            const colors = ['#cf2031', '#7c3aed', '#16a34a'];
            const labels = ['Connaître', 'Comprendre', 'Agir'];
            return (
              <div key={k} className="rounded-2xl p-3 border-2" style={{ borderColor: colors[i], background: `${colors[i]}0d` }}>
                <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: colors[i] }}>Objectif {i + 1} · {labels[i]}</div>
                <div className="text-sm mt-1">{casExample.objectifs?.[k]}</div>
              </div>
            );
          })}
        </div>
      );
    }
    return (
      <div className="rounded-2xl p-4 border-2 border-[#16a34a]/40 bg-[#16a34a]/5">
        <div className="text-[10px] font-black uppercase tracking-widest text-[#16a34a]">Évaluation-modèle</div>
        <div className="text-sm mt-2 leading-relaxed">{casExample.evaluationAttendue}</div>
        <div className="text-xs mt-3 text-muted-foreground">
          <strong>Méthodes :</strong> {casExample.methodesAttendues?.join(', ')} · <strong>Supports :</strong> {casExample.supportsAttendus?.slice(0, 2).join(', ')}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="mecanique-mode">
      <SessionHeader onBack={onBack} title="🧠 Mécanique 4 étapes" progress={`Étape ${step + 1} / 4`} accent="#0891b2" />

      {/* Cas fil rouge */}
      <Card className="border-2" style={{ borderColor: famMeta.color }}>
        <CardContent className="p-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Cas fil rouge · n°{casExample.num}</div>
          <div className="text-base font-black mt-1">{casExample.title}</div>
          <p className="text-xs italic text-muted-foreground mt-2">« {casExample.enonce.slice(0, 220)}... »</p>
        </CardContent>
      </Card>

      {/* Steps progress */}
      <div className="flex gap-2">
        {MECANIQUE_STEPS.map((s, i) => (
          <div
            key={s.id}
            className="flex-1 h-2 rounded-full transition-all"
            style={{ background: i <= step ? s.accent : 'hsl(var(--muted))' }}
          />
        ))}
      </div>

      {/* Étape courante */}
      <Card className="border-2" style={{ borderColor: current.accent }}>
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white" style={{ background: current.accent }}>
              <current.icon className="w-5 h-5" strokeWidth={2.4} />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: current.accent }}>Étape {current.id} / 4</div>
              <div className="text-lg font-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{current.title}</div>
            </div>
          </div>
          <p className="text-sm leading-relaxed text-foreground/85 mb-4" dangerouslySetInnerHTML={{ __html: current.teach.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
          {renderReveal()}
        </CardContent>
      </Card>

      {/* Nav */}
      <div className="flex gap-2 justify-between">
        <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="gap-1.5" data-testid="btn-mecanique-back">
          <ChevronLeft className="w-4 h-4" /> Précédente
        </Button>
        {step < MECANIQUE_STEPS.length - 1 ? (
          <Button onClick={() => setStep(step + 1)} className="gap-1.5" style={{ background: current.accent }} data-testid="btn-mecanique-next">
            Suivante <ChevronRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button onClick={onBack} className="gap-1.5" style={{ background: '#16a34a' }} data-testid="btn-mecanique-finish">
            <Award className="w-4 h-4" /> J&apos;ai compris !
          </Button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODE 4 — PROGRESSION (Tableau de bord)
// ─────────────────────────────────────────────────────────────────────────────

function ProgressionMode({ revision, onBack }) {
  const cas = useMemo(() => CAS_DETAILS.map((c) => {
    const card = revision.state.cards[c.num];
    return {
      num: c.num,
      title: c.title,
      familySlug: c.familySlug,
      level: card?.level ?? 0,
      attempts: card?.attempts ?? 0,
      wins: card?.wins ?? 0,
      lastSeenAt: card?.lastSeenAt,
    };
  }).sort((a, b) => a.level - b.level || a.num - b.num), [revision.state.cards]);

  return (
    <div className="space-y-4" data-testid="progression-mode">
      <SessionHeader onBack={onBack} title="📊 Ma progression" progress={`${cas.length} cas`} accent="#16a34a" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {cas.map((c) => {
          const fam = getFamilyMeta(c.familySlug);
          const color = LEVEL_COLORS[c.level] || '#9ca3af';
          return (
            <div
              key={c.num}
              className="rounded-2xl border bg-card p-3 flex items-center gap-3"
              style={{ borderLeftColor: color, borderLeftWidth: 4 }}
              data-testid={`progression-row-${c.num}`}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-black text-xs flex-shrink-0"
                style={{ background: color }}
              >
                {c.num}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate">{c.title}</div>
                <div className="text-[10px] text-muted-foreground flex items-center gap-2 mt-0.5">
                  <span style={{ color: fam.color }}>{fam.title}</span>
                  <span>·</span>
                  <span style={{ color }}>{LEVEL_LABELS[c.level]}</span>
                  {c.attempts > 0 && <><span>·</span><span>{c.wins}/{c.attempts}</span></>}
                </div>
              </div>
              <div className="text-[10px] font-black uppercase tracking-widest" style={{ color }}>
                L{c.level}
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-center mt-4">
        <Button
          variant="outline"
          onClick={() => { if (window.confirm('Réinitialiser toute ta progression ? (irréversible)')) revision.resetAll(); }}
          className="gap-1.5 text-[#cf2031]"
          data-testid="btn-reset-progression"
        >
          <RotateCcw className="w-4 h-4" /> Réinitialiser ma progression
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers de session
// ─────────────────────────────────────────────────────────────────────────────

const SessionHeader = ({ onBack, title, progress, accent, right }) => (
  <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="session-header">
    <Button variant="outline" size="sm" onClick={onBack} className="gap-1.5" data-testid="btn-back-hub">
      <ArrowLeft className="w-4 h-4" /> Retour
    </Button>
    <div className="text-center">
      <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: accent }}>{progress}</div>
      <div className="text-base font-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{title}</div>
    </div>
    <div className="min-w-[80px] flex justify-end">{right || null}</div>
  </div>
);

const FinishScreen = ({ title, subtitle, meta, accent, onBack }) => (
  <div className="space-y-4" data-testid="finish-screen">
    <Card className="border-2" style={{ borderColor: accent }}>
      <CardContent className="p-8 text-center">
        <Trophy className="w-12 h-12 mx-auto mb-3" style={{ color: accent }} />
        <h2 className="text-3xl font-black mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }} data-testid="finish-title">{title}</h2>
        <p className="text-lg" data-testid="finish-subtitle">{subtitle}</p>
        {meta && <p className="text-xs text-muted-foreground mt-2">{meta}</p>}
        <Button onClick={onBack} className="mt-5 gap-1.5" style={{ background: accent }} data-testid="btn-finish-back">
          <ArrowLeft className="w-4 h-4" /> Retour au hub
        </Button>
      </CardContent>
    </Card>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Composant principal
// ─────────────────────────────────────────────────────────────────────────────

export default function RevisionCasCCS() {
  useSeo({
    title: 'Réviser les 27 études de cas CCS — Mode interactif',
    description:
      "Apprends les 27 études de cas de l'oral CCS 2 Roues avec 4 modes interactifs : flashcards à mémoire espacée, quiz famille chronométré, tutoriel raisonnement et tableau de progression.",
    path: '/enseignant/revision-cas-ccs',
  });

  const revision = useRevisionCas();
  const [mode, setMode] = useState('hub'); // hub | flashcards | quiz-famille | mecanique | progression
  const stats = revision.getStats(ALL_CAS_NUMS);
  const presentation = usePresentationMode();

  // Slides auto-générés pour le Mode Projection : 1 slide d'intro + 27 cas
  const projectionSlides = useMemo(() => {
    const intro = [
      {
        kicker: '🎯 27 Études de cas CCS 2 Roues',
        title: 'Réviser en mode projection',
        body: '<p>Utilise <strong>← / →</strong> pour naviguer entre les cas.<br/>Appuie sur <strong>R</strong> pour revenir au début, <strong>ESC</strong> pour sortir.</p>',
        accent: '#cf2031',
      },
    ];
    const cases = CAS_DETAILS.map((c) => {
      const fam = getFamilyMeta(c.familySlug);
      return {
        kicker: `Cas n°${c.num} · Famille ${fam.title}`,
        title: c.title,
        body: `
          <p style="margin-bottom:1.5em">« ${c.enonce} »</p>
          <ul style="list-style:none;padding:0;margin:0">
            <li style="margin-bottom:0.6em"><strong style="color:#ef4444">Connaître</strong> · ${c.objectifs?.connaitre || ''}</li>
            <li style="margin-bottom:0.6em"><strong style="color:#a855f7">Comprendre</strong> · ${c.objectifs?.comprendre || ''}</li>
            <li><strong style="color:#22c55e">Agir</strong> · ${c.objectifs?.agir || ''}</li>
          </ul>
        `,
        accent: fam.color,
      };
    });
    return [...intro, ...cases];
  }, []);

  return (
    <article className="space-y-6" data-testid="revision-cas-page">
      {/* Breadcrumb + bouton projection */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <nav aria-label="Fil d'Ariane" className="flex items-center gap-2 text-xs text-muted-foreground">
          <Link to="/enseignant" className="hover:text-foreground">Espace Pro</Link>
          <span>›</span>
          <Link to="/enseignant/etudes-de-cas" className="hover:text-foreground">27 études de cas CCS</Link>
          <span>›</span>
          <span className="text-foreground font-bold">Mode Révision Pro</span>
        </nav>
        <PresentationButton onClick={presentation.toggle} active={presentation.active} label="Mode projection · 27 cas" />
      </div>

      {mode === 'hub' && <Hub stats={stats} setMode={setMode} />}
      {mode === 'flashcards' && <FlashcardsMode revision={revision} onBack={() => setMode('hub')} />}
      {mode === 'quiz-famille' && <QuizFamilleMode revision={revision} onBack={() => setMode('hub')} />}
      {mode === 'mecanique' && <MecaniqueMode onBack={() => setMode('hub')} />}
      {mode === 'progression' && <ProgressionMode revision={revision} onBack={() => setMode('hub')} />}

      {/* Overlay projection */}
      <PresentationOverlay
        active={presentation.active}
        exit={presentation.exit}
        slides={projectionSlides}
        title="Révision CCS 2R"
      />}
    </article>
  );
}
